import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import dynamic from "next/dynamic"
import {
  Container,
  TextSM,
  Badge,
  Box,
  ClockIcon,
  HStack,
  HeadingThree,
  Stack,
  Text,
  useTheme,
} from "datastory-shared/ui"
import { Section, PageHeader } from "datastory-shared/sections"
import {
  differenceInCalendarDays,
  addMonths,
  format,
} from "datastory-shared/utils/date"
import { useTranslation } from "datastory-shared/utils/i18n"
import { logEvent } from "datastory-shared/utils/analytics"
import { gql } from "datastory-shared/utils/graphql"
import { hasuraRequest } from "datastory-shared/utils/hasura"

import TrendsView from "./TrendsView"
import TrendsViewByParty from "./TrendsViewByParty"
import PollsTable from "./PollsTable"

const AudioPlayer = dynamic(() =>
  import("../../../blocks/AudioPlayer").then(module => {
    const { AudioPlayer } = module
    return AudioPlayer
  })
)

const daysUntilNextElection = differenceInCalendarDays(
  new Date(2026, 8, 13),
  new Date()
)

const PollOfPollsSweden = ({
  data: { polls, trends, metaPolls, metaParties },
}) => {
  const [data, setData] = useState({ polls, trends })
  const [isCompleteDataLoaded, setIsCompleteDataLoaded] = useState(false)
  const { t: i18t } = useTranslation("og_page--81503fa2")
  const { colors } = useTheme()

  useEffect(() => {
    setData({ polls, trends })
  }, [polls, trends])

  const t = key => i18t(key)

  const annotations = [
    {
      id: "four-percent-line",
      type: "line",
      holder: "bottom",
      y: 4,
      offsetX1: -10,
      style: {
        "stroke-dasharray": "3, 1.5",
        stroke: colors.gray[400],
        "stroke-width": 1.5,
      },
    },
  ]

  const sources = Array(metaPolls) && [
    { id: "mama", value: "mama", name: t("pop"), label: t("pop") },
    ...metaPolls,
  ]

  const xDomains = [
    {
      id: "complete",
      label: t("time-historical"),
      extent: [
        "",
        format(addMonths(new Date(), 4), {
          year: "numeric",
          month: "numeric",
          day: "numeric",
        }),
      ],
    },
    // {
    //   id: "since-last-election",
    //   label: t("time-since_last_election"),
    //   extent: [
    //     "2018-08-01",
    //     format(addMonths(new Date(), 4), {
    //       year: "numeric",
    //       month: "numeric",
    //       day: "numeric",
    //     }),
    //   ],
    // },
    {
      id: "6m",
      label: t("time-now"),
      extent: [
        "6m",
        format(addMonths(new Date(), 2), {
          year: "numeric",
          month: "numeric",
          day: "numeric",
        }),
      ],
    },
  ]

  // initial data is limited to the default domain
  // which is since the last election
  // load complete historical data only when needed
  // when the domain is changed to "complete"
  // to avoid huge initial document size (over 7MB)
  const onXDomainChange = async domain => {
    if (domain !== "complete" || isCompleteDataLoaded) return

    const data = await hasuraRequest(gql`
      query PollOfPollsSweden {
        polls: observation(
          where: {
            og_date: { _lt: "2018-08-01" }
            og_measureDimension: { _eq: "og:pollsterValuePercent" }
          }
          order_by: { og_date: asc }
        ) {
          og_date
          og_value
          og_politicalParty
          og_pollster
        }
        trends: observation(
          where: {
            og_date: { _lt: "2018-08-01" }
            og_measureDimension: { _eq: "og:pollOfPollsEstimatePercent" }
          }
          order_by: { og_date: asc }
        ) {
          og_date
          og_politicalParty
          og_value
        }
      }
    `)

    setIsCompleteDataLoaded(true)
    setData({
      polls: [...data.polls, ...polls],
      trends: [...data.trends, ...trends],
    })
  }

  return (
    <>
      <Section>
        <HStack justifyContent="center">
          <Box>
            <Badge colorScheme={"teal"} mr={"xxs"}>
              {`${daysUntilNextElection} ${t("days-left")}`}
            </Badge>
          </Box>
          <Box display={["none", "block"]}>
            <Badge
              alignItems="center"
              display="inline-flex"
              colorScheme={"teal"}
            >
              <ClockIcon mr="xxs" />
              {t("updated-frequency")}
            </Badge>
          </Box>
        </HStack>
        <PageHeader
          title={t("title")}
          lead={t("lead1") + " " + t("lead2")}
          pt={[0, 0]}
          data-cy="header"
        />
      </Section>
      <TrendsView
        polls={data.polls}
        trends={data.trends}
        metaPolls={metaPolls}
        metaParties={metaParties}
        yDomain={[0, 40]}
        xDomains={xDomains}
        onXDomainChange={onXDomainChange}
        annotations={annotations}
        sources={sources}
        isCompleteDataLoaded={isCompleteDataLoaded}
      />

      <Box my={["md", "lg", "xl"]}>
        <Container maxW="container.sm" mt={["sm", "md"]}>
          <Stack spacing="md">
            <TextSM px={["sm", "md", "lg", "xl"]}>{t("audio-intro")}</TextSM>
            <Box data-cy="polls-audio-player">
              <AudioPlayer
                title={t("audio-title")}
                imgUrl={`a9638059-f710-43fd-b663-abd2f84f8849.jpg`}
                audioUrl={`/static/mp3/1539165072-pollofpolls.mp3`}
                onPlay={() => {
                  logEvent(
                    "Audio",
                    "Played: Henrik Ekengren Oscarsson förklarar 'Mätningarnas mätning'",
                    "Polls"
                  )
                }}
              />
            </Box>
          </Stack>
        </Container>
      </Box>

      <Box>
        <Section maxW="container.md">
          <TextSM muted>{t("pbp-subtitle")}</TextSM>
          <Stack spacing="md">
            <HeadingThree>{t("pbp-title")}</HeadingThree>
            <Text>{t("pbp-text1")}</Text>
          </Stack>
        </Section>

        <TrendsViewByParty
          polls={data.polls}
          trends={data.trends}
          metaPolls={metaPolls}
          metaParties={metaParties}
          yDomain={[0, 45]}
          annotations={annotations}
          opacityNonSelected={0}
          sources={sources}
          isCompleteDataLoaded={isCompleteDataLoaded}
          xDomains={xDomains}
          onXDomainChange={onXDomainChange}
        />
      </Box>

      <Section maxW="container.md">
        <Stack spacing="md">
          <Text>{t("pbp-text2")}</Text>
          <Text>{t("pbp-text3")}</Text>
          <Box data-cy="polls-institutes-table">
            <PollsTable metaPolls={metaPolls} />
          </Box>
        </Stack>
      </Section>

      <Container size="container.sm" data-cy="section-method">
        <Stack spacing={["sm", "md"]}>
          <HeadingThree>{t("method-title")}</HeadingThree>
          <Text>{t("method-text1")}</Text>
          <Text>{t("method-text2")}</Text>
          <Text>{t("method-text3")}</Text>
        </Stack>
      </Container>

      <Container maxW="container.md" my={["md", "lg", "xl"]}>
        <Text>{t("contact-text")}</Text>
      </Container>
    </>
  )
}

PollOfPollsSweden.propTypes = {
  data: PropTypes.object.isRequired,
}

export default PollOfPollsSweden
